import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { builderSteps, type BuilderStep } from "../../stores/metadata-builder-store";
import { cn } from "../../lib/utils";

type BuilderStepNavProp = {
  step: BuilderStep;
  steps: Record<BuilderStep, { title: string }>;
  canNavigate: (target: number) => boolean;
  navigate: (target: number) => void;
};

function stepPillClass(position: number, index: number) {
  if (position === index) return "bg-neutral-900 text-white shadow-xs";
  if (position < index) return "bg-neutral-100 text-neutral-700 hover:bg-neutral-200";
  return "text-neutral-400 hover:bg-neutral-50";
}

const arrowClass = "flex items-center justify-center rounded px-1.5 py-1 text-neutral-600 transition-colors duration-200 enabled:hover:bg-neutral-200 enabled:hover:text-neutral-950 disabled:opacity-40 [&_svg]:size-3.5";

export function BuilderStepNav({ step, steps, canNavigate, navigate }: BuilderStepNavProp) {
  const { t } = useTranslation();
  const index = builderSteps.indexOf(step);
  const total = builderSteps.length;
  const isFirst = index === 0;
  const isLast = index === total - 1;
  const canGoBack = !isFirst;
  const canGoNext = !isLast && canNavigate(index + 1);
  return <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white select-none">
    <div className="flex items-center gap-2 border-b border-neutral-100 bg-neutral-50/60 px-3.5 py-2">
      <div className="flex min-w-0 items-center gap-2">
        <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-neutral-400">{t("builder.stepIndicator", { current: index + 1, total })}</span>
        <span className="text-neutral-300">/</span>
        <span className="truncate text-xs font-bold text-neutral-900">{steps[step].title}</span>
      </div>
    </div>
    <div className="grid grid-cols-[auto_1fr_auto] items-center gap-1 p-2">
      <button type="button" title={t("builder.back")} disabled={!canGoBack} onClick={() => navigate(index - 1)} className={arrowClass}><ChevronLeft /></button>
      <ol className="grid grid-cols-7 gap-1">{builderSteps.map((key, position) => <li key={key}>
        <button type="button" title={steps[key].title} disabled={!canNavigate(position)} onClick={() => navigate(position)}
          className={cn("w-full rounded py-1 text-center text-[11px] font-semibold tabular-nums transition-colors duration-200 disabled:opacity-40", stepPillClass(position, index))}>
          {position + 1}
        </button>
      </li>)}</ol>
      <button type="button" title={t("builder.next")} disabled={!canGoNext} onClick={() => navigate(index + 1)} className={arrowClass}><ChevronRight /></button>
    </div>
  </div>;
}
